import React, { useState } from 'react';
import { MapPin, Users, Phone, Mail, Search, ChevronRight, Heart, GraduationCap, Briefcase, Award } from 'lucide-react';

interface Federation {
    id: number;
    name: string;
    type: 'ligue' | 'federation';
    province?: string;
    city?: string;
    president: string;
    phone: string;
    email: string;
    address?: string;
    members?: number;
    icon?: React.ComponentType<any>;
}

const FederationsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('ligues');
    const [searchTerm, setSearchTerm] = useState('');

    // Les 9 provinces du Gabon avec leurs informations
    const provinces = [
        {
            id: 'estuaire',
            name: 'Estuaire',
            displayName: 'Province de l\'Estuaire',
            imageUrl: 'estuaire.jpg'
        },
        {
            id: 'haut-ogooue',
            name: 'Haut-Ogooué',
            displayName: 'Province du Haut-Ogooué',
            imageUrl: 'haut-ogooue.jpg'
        },
        {
            id: 'moyen-ogooue',
            name: 'Moyen-Ogooué',
            displayName: 'Province du Moyen-Ogooué',
            imageUrl: 'moyen-ogooue.jpg'
        },
        {
            id: 'ngounie',
            name: 'Ngounié',
            displayName: 'Province de la Ngounié',
            imageUrl: 'ngounie.jpg'
        },
        {
            id: 'nyanga',
            name: 'Nyanga',
            displayName: 'Province de la Nyanga',
            imageUrl: 'nyanga.jpg'
        },
        {
            id: 'ogooue-ivindo',
            name: 'Ogooué-Ivindo',
            displayName: 'Province de l\'Ogooué-Ivindo',
            imageUrl: 'ogooue-ivindo.jpg'
        },
        {
            id: 'ogooue-lolo',
            name: 'Ogooué-Lolo',
            displayName: 'Province de l\'Ogooué-Lolo',
            imageUrl: 'ogooue-lolo.jpg'
        },
        {
            id: 'ogooue-maritime',
            name: 'Ogooué-Maritime',
            displayName: 'Province de l\'Ogooué-Maritime',
            imageUrl: 'ogooue-maritime.jpg'
        },
        {
            id: 'woleu-ntem',
            name: 'Woleu-Ntem',
            displayName: 'Province du Woleu-Ntem',
            imageUrl: 'woleu-ntem.jpg'
        }
    ];

    // Données des ligues nationales avec icônes
    const ligues: Federation[] = [
        {
            id: 1,
            name: "Ligue des Femmes Volontaires",
            type: 'ligue',
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "femmes@udb.ga",
            members: 15000,
            icon: Heart
        },
        {
            id: 2,
            name: "Ligue des Jeunes Volontaires",
            type: 'ligue',
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "jeunes@udb.ga",
            members: 25000,
            icon: Users
        },
        {
            id: 3,
            name: "Ligue des Cadres et Intellectuels Volontaires",
            type: 'ligue',
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "cadres@udb.ga",
            members: 8000,
            icon: GraduationCap
        },
        {
            id: 4,
            name: "Ligue des Travailleurs Volontaires",
            type: 'ligue',
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "travailleurs@udb.ga",
            members: 12000,
            icon: Briefcase
        }
    ];

    // Données des fédérations par province
    const federations: Federation[] = [
        // Estuaire
        {
            id: 10,
            name: "Fédération de Libreville",
            type: 'federation',
            province: "Estuaire",
            city: "Libreville",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "libreville@udb.ga",
            address: "BP 1234, Quartier Louis",
            members: 5000
        },
        {
            id: 11,
            name: "Fédération d'Owendo",
            type: 'federation',
            province: "Estuaire",
            city: "Owendo",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "owendo@udb.ga",
            address: "Avenue du Port, Owendo",
            members: 2000
        },
        {
            id: 12,
            name: "Fédération d'Akanda",
            type: 'federation',
            province: "Estuaire",
            city: "Akanda",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "akanda@udb.ga",
            address: "Centre-ville, Akanda",
            members: 1500
        },
        {
            id: 13,
            name: "Fédération de Ntoum",
            type: 'federation',
            province: "Estuaire",
            city: "Ntoum",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "ntoum@udb.ga",
            address: "Carrefour Ntoum",
            members: 800
        },
        // Haut-Ogooué
        {
            id: 20,
            name: "Fédération de Franceville",
            type: 'federation',
            province: "Haut-Ogooué",
            city: "Franceville",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "franceville@udb.ga",
            address: "Centre-ville, Franceville",
            members: 3000
        },
        {
            id: 21,
            name: "Fédération de Moanda",
            type: 'federation',
            province: "Haut-Ogooué",
            city: "Moanda",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "moanda@udb.ga",
            address: "Quartier Comilog",
            members: 1500
        },
        {
            id: 22,
            name: "Fédération de Mounana",
            type: 'federation',
            province: "Haut-Ogooué",
            city: "Mounana",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "mounana@udb.ga",
            address: "Centre Minier",
            members: 600
        },
        // Moyen-Ogooué
        {
            id: 30,
            name: "Fédération de Lambaréné",
            type: 'federation',
            province: "Moyen-Ogooué",
            city: "Lambaréné",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "lambarene@udb.ga",
            address: "Quartier Isaac",
            members: 2200
        },
        {
            id: 31,
            name: "Fédération de Ndjolé",
            type: 'federation',
            province: "Moyen-Ogooué",
            city: "Ndjolé",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "ndjole@udb.ga",
            address: "Centre administratif",
            members: 900
        },
        // Ngounié
        {
            id: 40,
            name: "Fédération de Mouila",
            type: 'federation',
            province: "Ngounié",
            city: "Mouila",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "mouila@udb.ga",
            address: "Avenue de l'Indépendance",
            members: 1800
        },
        {
            id: 41,
            name: "Fédération de Ndendé",
            type: 'federation',
            province: "Ngounié",
            city: "Ndendé",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "ndende@udb.ga",
            address: "Quartier Commercial",
            members: 700
        },
        {
            id: 42,
            name: "Fédération de Mandji",
            type: 'federation',
            province: "Ngounié",
            city: "Mandji",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "mandji@udb.ga",
            address: "Zone Industrielle",
            members: 500
        },
        // Nyanga
        {
            id: 50,
            name: "Fédération de Tchibanga",
            type: 'federation',
            province: "Nyanga",
            city: "Tchibanga",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "tchibanga@udb.ga",
            address: "Centre-ville",
            members: 1200
        },
        {
            id: 51,
            name: "Fédération de Mayumba",
            type: 'federation',
            province: "Nyanga",
            city: "Mayumba",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "mayumba@udb.ga",
            address: "Bord de mer",
            members: 600
        },
        // Ogooué-Ivindo
        {
            id: 60,
            name: "Fédération de Makokou",
            type: 'federation',
            province: "Ogooué-Ivindo",
            city: "Makokou",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "makokou@udb.ga",
            address: "Quartier Sibang",
            members: 1500
        },
        {
            id: 61,
            name: "Fédération de Booué",
            type: 'federation',
            province: "Ogooué-Ivindo",
            city: "Booué",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "booue@udb.ga",
            address: "Gare ferroviaire",
            members: 800
        },
        // Ogooué-Lolo
        {
            id: 70,
            name: "Fédération de Koulamoutou",
            type: 'federation',
            province: "Ogooué-Lolo",
            city: "Koulamoutou",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "koulamoutou@udb.ga",
            address: "Avenue Centrale",
            members: 1300
        },
        {
            id: 71,
            name: "Fédération de Lastoursville",
            type: 'federation',
            province: "Ogooué-Lolo",
            city: "Lastoursville",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "lastoursville@udb.ga",
            address: "Quartier Doumé",
            members: 600
        },
        // Ogooué-Maritime
        {
            id: 80,
            name: "Fédération de Port-Gentil",
            type: 'federation',
            province: "Ogooué-Maritime",
            city: "Port-Gentil",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "portgentil@udb.ga",
            address: "Centre-ville maritime",
            members: 4000
        },
        {
            id: 81,
            name: "Fédération d'Omboué",
            type: 'federation',
            province: "Ogooué-Maritime",
            city: "Omboué",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "omboue@udb.ga",
            address: "Quartier Lac",
            members: 700
        },
        {
            id: 82,
            name: "Fédération de Gamba",
            type: 'federation',
            province: "Ogooué-Maritime",
            city: "Gamba",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "gamba@udb.ga",
            address: "Zone pétrolière",
            members: 900
        },
        // Woleu-Ntem
        {
            id: 90,
            name: "Fédération d'Oyem",
            type: 'federation',
            province: "Woleu-Ntem",
            city: "Oyem",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "oyem@udb.ga",
            address: "Centre administratif",
            members: 2500
        },
        {
            id: 91,
            name: "Fédération de Bitam",
            type: 'federation',
            province: "Woleu-Ntem",
            city: "Bitam",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "bitam@udb.ga",
            address: "Frontière Nord",
            members: 1200
        },
        {
            id: 92,
            name: "Fédération de Minvoul",
            type: 'federation',
            province: "Woleu-Ntem",
            city: "Minvoul",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "minvoul@udb.ga",
            address: "Centre-ville",
            members: 500
        },
        {
            id: 93,
            name: "Fédération de Mitzic",
            type: 'federation',
            province: "Woleu-Ntem",
            city: "Mitzic",
            president: "Alain Moukagni",
            phone: "+241 66 63 34 40",
            email: "mitzic@udb.ga",
            address: "Carrefour Mitzic",
            members: 800
        }
    ];

    // Filtrer les fédérations
    const getFilteredFederations = () => {
        if (activeTab === 'ligues') {
            return ligues.filter(ligue =>
                ligue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ligue.president.toLowerCase().includes(searchTerm.toLowerCase())
            );
        } else {
            const provinceName = provinces.find(p => p.id === activeTab)?.name || activeTab;
            return federations.filter(fed =>
                fed.province === provinceName &&
                (fed.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    fed.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    fed.president.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
    };

    const filteredData = getFilteredFederations();

    // Compter le nombre total de fédérations par province
    const getFederationCount = (provinceId: string) => {
        const provinceName = provinces.find(p => p.id === provinceId)?.name || provinceId;
        return federations.filter(fed => fed.province === provinceName).length;
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section flat design avec pattern */}
            <div className="bg-primary-blue text-white py-24 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 70px)`
                    }}></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl md:text-6xl font-light mb-4">
                            Nos Fédérations
                        </h1>
                        <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
                        <p className="text-xl font-light opacity-90">
                            Un réseau national au service de tous les Gabonais
                        </p>
                    </div>
                </div>
            </div>

            {/* Contenu principal avec sidebar */}
            <div className="container mx-auto px-4 py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar avec onglets verticaux - design plus moderne */}
                    <div className="lg:w-80">
                        <div className="bg-gray-50 p-6 sticky top-24">
                            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
                                Explorer par région
                            </h2>

                            {/* Onglet Ligues */}
                            <button
                                onClick={() => setActiveTab('ligues')}
                                className={`w-full text-left px-4 py-3 mb-1 transition-all flex items-center justify-between ${
                                    activeTab === 'ligues'
                                        ? 'bg-primary-green text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex items-center">
                                    <Award className="w-5 h-5 mr-3" />
                                    <span className="font-medium">Ligues Nationales</span>
                                </div>
                                {activeTab === 'ligues' && <ChevronRight className="w-4 h-4" />}
                            </button>

                            <div className="h-px bg-gray-200 my-4"></div>

                            {/* Onglets Provinces */}
                            <div className="space-y-1">
                                {provinces.map((province) => {
                                    const count = getFederationCount(province.id);
                                    return (
                                        <button
                                            key={province.id}
                                            onClick={() => setActiveTab(province.id)}
                                            className={`w-full text-left px-4 py-3 transition-all flex items-center justify-between ${
                                                activeTab === province.id
                                                    ? 'bg-primary-green text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className="flex items-center">
                                                <MapPin className="w-5 h-5 mr-3" />
                                                <span className="font-medium">{province.name}</span>
                                            </div>
                                            <div className="flex items-center">
                                                {count > 0 && (
                                                    <span className={`text-xs font-bold mr-2 ${
                                                        activeTab === province.id ? 'text-primary-yellow' : 'text-gray-500'
                                                    }`}>
                                                        {count}
                                                    </span>
                                                )}
                                                {activeTab === province.id && <ChevronRight className="w-4 h-4" />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="flex-1">
                        {/* Statistiques flat design avec couleurs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            <div className="bg-white border-2 border-primary-blue p-6 text-center">
                                <div className="text-3xl font-bold text-primary-blue mb-1">9</div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Provinces</p>
                            </div>
                            <div className="bg-white border-2 border-primary-green p-6 text-center">
                                <div className="text-3xl font-bold text-primary-green mb-1">
                                    {federations.length}
                                </div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Fédérations</p>
                            </div>
                            <div className="bg-white border-2 border-primary-yellow p-6 text-center">
                                <div className="text-3xl font-bold text-primary-yellow mb-1">4</div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Ligues</p>
                            </div>
                            <div className="bg-white border-2 border-gray-400 p-6 text-center">
                                <div className="text-3xl font-bold text-gray-700 mb-1">60k+</div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Membres</p>
                            </div>
                        </div>

                        {/* Barre de recherche flat */}
                        <div className="mb-8">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher une fédération, une ville, un responsable..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-gray-200 focus:outline-none focus:border-primary-blue transition-colors"
                                />
                            </div>
                        </div>

                        {/* Titre de la section active avec écusson */}
                        <div className="mb-6 bg-gray-50 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    {activeTab !== 'ligues' && (() => {
                                        const province = provinces.find(p => p.id === activeTab);
                                        return province ? (
                                            <img
                                                key={activeTab}
                                                src={`/images/provinces/${province.imageUrl}`}
                                                alt={`Écusson ${province.name}`}
                                                className="w-16 h-16 object-contain"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                }}
                                            />
                                        ) : null;
                                    })()}
                                    <div>
                                        <h2 className="text-2xl font-light text-gray-900">
                                            {activeTab === 'ligues'
                                                ? 'Ligues Nationales'
                                                : provinces.find(p => p.id === activeTab)?.displayName || 'Province'}
                                        </h2>
                                        <p className="text-sm text-gray-500">
                                            {filteredData.length} {activeTab === 'ligues' ? 'ligue' : 'fédération'}{filteredData.length > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                                {activeTab === 'ligues' && (
                                    <div className="text-right">
                                        <p className="text-sm text-gray-500">Organisations nationales</p>
                                        <p className="text-xs text-gray-400">Présentes dans toutes les provinces</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Grille de cartes flat design */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {filteredData.length > 0 ? (
                                filteredData.map((item) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <div
                                            key={item.id}
                                            className="bg-white border-2 border-gray-200 p-6 hover:border-primary-blue transition-colors"
                                        >
                                            {/* En-tête de la carte avec icône pour les ligues */}
                                            <div className="mb-4 pb-4 border-b border-gray-100">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                            {item.name}
                                                        </h3>
                                                        <div className="flex items-center space-x-4 text-sm">
                                                            {item.city && (
                                                                <span className="text-gray-600">
                                                                    <MapPin className="w-4 h-4 inline mr-1" />
                                                                    {item.city}
                                                                </span>
                                                            )}
                                                            {item.members && (
                                                                <span className="text-primary-blue font-semibold">
                                                                    {item.members.toLocaleString()} membres
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {IconComponent && (
                                                        <div className="ml-4 bg-gray-100 p-3">
                                                            <IconComponent className="w-6 h-6 text-primary-green" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Informations du président */}
                                            <div className="mb-4">
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Président</p>
                                                <p className="text-gray-900 font-medium">{item.president}</p>
                                            </div>

                                            {/* Coordonnées */}
                                            <div className="space-y-2 text-sm">
                                                <a href={`tel:${item.phone}`} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                                                    <Phone className="w-4 h-4 mr-2" />
                                                    <span>{item.phone}</span>
                                                </a>
                                                <a href={`mailto:${item.email}`} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                                                    <Mail className="w-4 h-4 mr-2" />
                                                    <span className="break-all">{item.email}</span>
                                                </a>
                                                {item.address && (
                                                    <div className="flex items-start text-gray-600">
                                                        <MapPin className="w-4 h-4 mr-2 mt-0.5" />
                                                        <span>{item.address}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Bouton d'action */}
                                            <button className="mt-6 w-full bg-primary-green text-white py-3 px-4 font-medium hover:bg-primary-blue transition-colors">
                                                Contacter
                                            </button>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="col-span-full text-center py-16 bg-gray-50 border-2 border-gray-200">
                                    <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-600">
                                        Aucun résultat pour "{searchTerm}"
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Message d'information flat */}
                        {activeTab !== 'ligues' && filteredData.length > 0 && (
                            <div className="mt-8 bg-blue-50 border-l-4 border-primary-blue p-4">
                                <p className="text-gray-700">
                                    <strong className="font-semibold">Information :</strong> Cette liste n'est pas exhaustive.
                                    Contactez le siège national au <strong className="font-semibold">+241 66 63 34 40</strong> pour plus d'informations.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section CTA flat design */}
                <div className="mt-16 bg-primary-green text-white p-12">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-light mb-4">
                            Créer une fédération dans votre ville
                        </h2>
                        <div className="w-16 h-1 bg-primary-yellow mx-auto mb-6"></div>
                        <p className="text-lg font-light opacity-90 mb-8">
                            Vous souhaitez créer une fédération locale ? Nous vous accompagnons dans toutes les démarches.
                        </p>
                        <button className="bg-white text-primary-green px-8 py-3 font-semibold hover:bg-gray-100 transition-colors">
                            NOUS CONTACTER
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FederationsPage;
